/* eslint-disable react-refresh/only-export-components */
import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { withJsonFormsControlProps } from '@jsonforms/react';
import {
  and,
  type ControlProps,
  optionIs,
  rankWith,
  uiTypeIs,
  type JsonFormsRendererRegistryEntry,
} from '@jsonforms/core';
import { httpClient } from '@/utils/httpClient';
import { logger } from '@equip/utils/logger';

const log = logger.child({ component: 'EntitySelectRenderer' });

interface DataSourceConfig {
  endpoint: string;
  params?: Record<string, string>;
}

interface EntitySelectOptions {
  variant?: string;
  allowClear?: boolean;
  dataSource?: DataSourceConfig;
  valueField?: string;
  labelField?: string;
  descriptionField?: string;
  labelTemplate?: string;
  emptyOptionLabel?: string;
}

interface EntitySelectOption {
  value: string;
  label: string;
  description?: string;
  raw: Record<string, unknown>;
}

const ENTITY_SELECT_VARIANT = 'entitySelect';

const getValueFromPath = (record: Record<string, unknown>, path?: string) => {
  if (!path) return undefined;
  const segments = path.split('.').filter(Boolean);
  let current: unknown = record;
  for (const segment of segments) {
    if (current === null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return current ?? undefined;
};

const formatTemplate = (template: string, record: Record<string, unknown>) => {
  return template.replace(/{{\s*([^}]+)\s*}}/g, (_match, group) => {
    const value = getValueFromPath(record, group.trim());
    return value === undefined || value === null ? '' : String(value);
  });
};

const buildOption = (
  record: Record<string, unknown>,
  options: EntitySelectOptions,
): EntitySelectOption => {
  const valueField = options.valueField ?? 'id';
  const labelField = options.labelField ?? 'display_name';
  const descriptionField = options.descriptionField;
  const labelTemplate = options.labelTemplate;
  const rawValue = getValueFromPath(record, valueField);
  const resolvedValue = rawValue ? String(rawValue) : '';
  const defaultLabel = getValueFromPath(record, labelField);
  const label = labelTemplate
    ? formatTemplate(labelTemplate, record)
    : typeof defaultLabel === 'string' && defaultLabel.length > 0
      ? defaultLabel
      : ((record.entity_identifier as string) ?? resolvedValue);
  const description = descriptionField ? getValueFromPath(record, descriptionField) : undefined;
  return {
    value: resolvedValue,
    label: label || 'Unnamed option',
    description: description ? String(description) : undefined,
    raw: record,
  };
};

const dataKeyFromOptions = (options: EntitySelectOptions) => {
  if (!options.dataSource) return 'entity-select::no-source';
  return JSON.stringify(options.dataSource);
};

const EntitySelectControl = ({
  data,
  handleChange,
  path,
  uischema,
  label,
  schema,
  visible = true,
  enabled = true,
  errors,
}: ControlProps) => {
  const selectOptions = useMemo(() => {
    const provided = (uischema.options ?? {}) as EntitySelectOptions;
    if (provided.variant !== ENTITY_SELECT_VARIANT) {
      return undefined;
    }
    return {
      ...provided,
      allowClear: provided.allowClear ?? true,
    };
  }, [uischema.options]);
  const allowClear = selectOptions?.allowClear ?? true;
  const [options, setOptions] = useState<EntitySelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fallbackLabel, setFallbackLabel] = useState<string | null>(null);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const dataKey = useMemo(
    () => (selectOptions ? dataKeyFromOptions(selectOptions) : undefined),
    [selectOptions],
  );

  useEffect(() => {
    let isMounted = true;
    const fetchOptions = async () => {
      if (!selectOptions?.dataSource?.endpoint) {
        return;
      }
      setLoading(true);
      setLoadError(null);
      try {
        const params = new URLSearchParams(selectOptions.dataSource.params ?? {});
        const query = params.toString();
        const endpoint = query
          ? `${selectOptions.dataSource.endpoint}?${query}`
          : selectOptions.dataSource.endpoint;
        const result = await httpClient.get<Record<string, unknown>[]>(endpoint);
        if (!isMounted) return;
        const rows = Array.isArray(result) ? result : [];
        const baseOptions = rows.map((row) => buildOption(row, selectOptions));
        if (selectOptions.emptyOptionLabel) {
          baseOptions.unshift({
            value: '',
            label: selectOptions.emptyOptionLabel,
            raw: {},
          });
        }
        setOptions(baseOptions);
        setOptionsLoaded(true);
      } catch (error) {
        if (!isMounted) return;
        const message = error instanceof Error ? error.message : 'Failed to load options';
        log.warn('EntitySelect fetch failed', { endpoint: selectOptions.dataSource?.endpoint, error: message });
        setLoadError(message);
        setOptions([]);
        setOptionsLoaded(true);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (!selectOptions) {
      setOptions([]);
      setLoadError('Entity select renderer missing configuration.');
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    if (!selectOptions.dataSource?.endpoint) {
      setOptions([]);
      setLoadError('Entity select renderer requires a dataSource endpoint.');
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    void fetchOptions();
    return () => {
      isMounted = false;
    };
  }, [dataKey, selectOptions]);

  // When disabled (readonly) and the current value isn't in the prefetched options,
  // fetch the specific entity so we can display a human-readable label instead of a UUID.
  useEffect(() => {
    let isMounted = true;

    if (
      enabled ||
      !data ||
      !selectOptions?.dataSource?.endpoint ||
      !optionsLoaded
    ) {
      if (enabled || !data) setFallbackLabel(null);
      return () => {
        isMounted = false;
      };
    }

    const normalized = String(data);
    if (options.find((opt) => opt.value === normalized)) {
      setFallbackLabel(null);
      return () => {
        isMounted = false;
      };
    }

    const valueField = selectOptions.valueField ?? 'id';
    const labelField = selectOptions.labelField ?? 'display_name';
    // For dot-notation labelFields like "entity_data.type_name", request the
    // root column (entity_data) so PostgREST returns the full JSON object and
    // getValueFromPath can traverse into it.
    const selectLabelField = labelField.includes('.') ? (labelField.split('.')[0] ?? labelField) : labelField;
    const endpoint = `${selectOptions.dataSource.endpoint}?${valueField}=eq.${encodeURIComponent(normalized)}&select=${encodeURIComponent(selectLabelField)},${encodeURIComponent(valueField)}`;

    void (async () => {
      try {
        const rows = await httpClient.get<Record<string, unknown>[]>(endpoint);
        const first = rows[0];
        if (isMounted && first) {
          const resolved = getValueFromPath(first, labelField);
          setFallbackLabel(resolved ? String(resolved) : null);
        }
      } catch {
        // Silently fall back to UUID display
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [enabled, data, options, optionsLoaded, selectOptions]);

  const selectedOption = useMemo(() => {
    if (!data) {
      if (selectOptions?.emptyOptionLabel) {
        return { value: '', label: selectOptions.emptyOptionLabel, raw: {} };
      }
      return null;
    }
    const normalized = String(data);
    const existing = options.find((option) => option.value === normalized);
    if (existing) return existing;
    return {
      value: normalized,
      label: fallbackLabel ?? normalized,
      raw: {},
    };
  }, [data, options, selectOptions?.emptyOptionLabel, fallbackLabel]);

  if (!visible) {
    return null;
  }

  const helperText = errors ? errors : loadError;
  const resolvedLabel = label ?? (schema?.title as string | undefined) ?? '';

  if (!selectOptions || !selectOptions.dataSource?.endpoint) {
    const fallbackValue = data === null || data === undefined ? '' : String(data);
    return (
      <TextField
        label={resolvedLabel}
        value={fallbackValue}
        error
        helperText={helperText ?? 'Entity select renderer misconfigured.'}
        fullWidth
        disabled
      />
    );
  }

  return (
    <Autocomplete<EntitySelectOption, false, boolean, false>
      options={options}
      value={selectedOption ?? null}
      onChange={(_event, newValue) => {
        if (!newValue) {
          handleChange(path, null);
          return;
        }
        const resolvedValue = newValue.value === '' ? null : newValue.value;
        handleChange(path, resolvedValue);
      }}
      fullWidth
      getOptionLabel={(option) => option?.label ?? ''}
      isOptionEqualToValue={(option, value) => option.value === value?.value}
      loading={loading}
      disabled={!enabled}
      clearOnBlur
      handleHomeEndKeys
      disableClearable={!allowClear}
      renderInput={(params) => (
        <TextField
          {...params}
          label={resolvedLabel}
          error={Boolean(errors)}
          helperText={helperText}
          fullWidth
          inputProps={{
            ...params.inputProps,
            style: { textAlign: 'left' },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2">{option.label}</Typography>
          {option.description && (
            <Typography variant="caption" color="text.secondary">
              {option.description}
            </Typography>
          )}
        </Box>
      )}
    />
  );
};

const EntitySelectControlRenderer = withJsonFormsControlProps(EntitySelectControl);

/**
 * RENDERER RANKING STRATEGY:
 * JSONForms uses a ranking system to determine which renderer to use.
 * Higher ranks take priority over lower ranks.
 *
 * Current Ranking Hierarchy:
 * - Rank 1-3:   Core JSONForms default renderers
 * - Rank 4:     Custom control renderers (TextAreaRenderer)
 * - Rank 5:     Custom layout/specialized renderers (GroupLayoutRenderer, this EntitySelectRenderer)
 * - Rank 10+:   Reserved for future highly-specific renderers
 *
 * This renderer uses rank 5 to activate for Controls with variant='entitySelect'.
 * It provides autocomplete functionality for selecting entities from API endpoints.
 */
const baseVariantTester = and(uiTypeIs('Control'), optionIs('variant', ENTITY_SELECT_VARIANT));

export const entitySelectRendererEntry: JsonFormsRendererRegistryEntry = {
  tester: rankWith(5, baseVariantTester),
  renderer: EntitySelectControlRenderer,
};
